import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

// ============================================================================
// Types
// ============================================================================

export type InquiryCategory = 'learning' | 'technical' | 'account' | 'other'
export type InquiryStatus = 'pending' | 'answered' | 'closed'

export interface AdminInquiryListItem {
  inquiryId: number
  organizationName: string | null
  userName: string | null
  userEmail: string | null
  category: string
  title: string
  status: string
  replyCount: number
  createdAt: string
}

export interface InquiryListResponse {
  content: AdminInquiryListItem[]
  totalElements: number
  totalPages: number
  page: number
  size: number
}

export interface AttachmentResponse {
  attachmentId: number
  fileName: string
  fileSize: number
  downloadUrl: string | null
}

export interface ReplyResponse {
  replyId: number
  authorName: string
  authorRole: string
  content: string
  createdAt: string
}

export interface AdminInquiryDetailResponse {
  inquiryId: number
  organizationName: string | null
  userName: string | null
  userEmail: string | null
  category: string
  title: string
  content: string
  status: string
  createdAt: string
  attachments: AttachmentResponse[]
  replies: ReplyResponse[]
}

export interface InquiryStats {
  pending: number
  answered: number
  closed: number
}

// ============================================================================
// API Functions
// ============================================================================

export const supportInquiryApi = {
  getList: async (params: {
    status?: InquiryStatus
    category?: InquiryCategory
    page?: number
    size?: number
  } = {}): Promise<InquiryListResponse> => {
    const response = await apiClient.get<ApiResponse<InquiryListResponse>>(
      '/admin/support/inquiries',
      { params }
    )
    if (!response.data.data) {
      throw new Error('Failed to fetch inquiries')
    }
    return response.data.data
  },

  getById: async (id: number): Promise<AdminInquiryDetailResponse> => {
    const response = await apiClient.get<ApiResponse<AdminInquiryDetailResponse>>(
      `/admin/support/inquiries/${id}`
    )
    if (!response.data.data) {
      throw new Error('Inquiry not found')
    }
    return response.data.data
  },

  createReply: async (inquiryId: number, content: string): Promise<ReplyResponse> => {
    const response = await apiClient.post<ApiResponse<ReplyResponse>>(
      `/admin/support/inquiries/${inquiryId}/replies`,
      { content }
    )
    if (!response.data.data) {
      throw new Error('Failed to create reply')
    }
    return response.data.data
  },

  updateStatus: async (inquiryId: number, status: string): Promise<void> => {
    await apiClient.patch(
      `/admin/support/inquiries/${inquiryId}/status`,
      { status }
    )
  },

  getStats: async (): Promise<InquiryStats> => {
    const response = await apiClient.get<ApiResponse<InquiryStats>>(
      '/admin/support/inquiries/stats'
    )
    if (!response.data.data) {
      throw new Error('Failed to fetch stats')
    }
    return response.data.data
  },
}
